import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Sun, Moon, Users, BarChart3, Bot, CheckCircle, Send } from 'lucide-react';
import LogoVertical from '../assets/images/logo-vertical.png';
import LogoDarkVertical from '../assets/images/logo-dark.png';
import { SendVerificationCodeRequestDTO, VerifyCodeRequestDTO } from '../interfaces/AuthInterface';
import { sendVerificationCode, verifyCode } from '../services/AuthService';
import SessionService from '../services/SessionService';
import { updateUserThemePreference } from '../services/UserService';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/LoginScreen/LoginScreen.css';

const STORAGE_KEY = '@ligchat/themePreference';

const LoginScreen: React.FC = () => {
  const { resolvedTheme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [activeTab, setActiveTab] = useState('email');
  const [rememberMe, setRememberMe] = useState(false);
  const [timer, setTimer] = useState(0);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.disconnected) {
      setIsModalVisible(true);
    }
  }, [location.state]);

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(value);
    setIsEmailValid(isValid);
    setIsFormValid(isValid && value.trim() !== '');
  };

  const validateCode = (value: string) => {
    setIsCodeValid(value.length === 6);
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      setLoadingEmail(true);
      try {
        const requestData: SendVerificationCodeRequestDTO = {
          email,
          phoneNumber: '',
        };
        await sendVerificationCode(requestData);
        setActiveTab('code');
      } catch (error: any) {
        setIsEmailValid(false);
        setErrorMessage('E-mail não encontrado');
      } finally {
        setLoadingEmail(false);
      }
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCodeValid) {
      setLoadingCode(true);
      try {
        const requestData: VerifyCodeRequestDTO = {
          email,
          code,
          rememberMe,
        };
        await verifyCode(requestData);
        // Sincronizar tema com backend após login
        try {
          const savedTheme = localStorage.getItem(STORAGE_KEY) as 'light' | 'dark' | 'system' | null;
          const themeToSync = savedTheme && ['light', 'dark', 'system'].includes(savedTheme) ? savedTheme : 'dark';
          await updateUserThemePreference(themeToSync);
        } catch {
          // Ignora falha ao sincronizar tema
        }
        navigate('/dashboard');
      } catch (error) {
        setIsCodeValid(false);
        setErrorMessage('Código inválido');
      } finally {
        setLoadingCode(false);
      }
    }
  };

  useEffect(() => {
    const token = SessionService.getToken();
    if (token) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleResendCode = () => {
    setTimer(30);
    const requestData: SendVerificationCodeRequestDTO = {
      email,
      phoneNumber: '',
    };
    sendVerificationCode(requestData).catch(() => { });
  };

  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer]);

  return (
    <div className="loginScreen-layout">
      {/* Theme toggle - top right */}
      <button
        className="loginScreen-themeToggle"
        onClick={toggleTheme}
        title={resolvedTheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
        aria-label={resolvedTheme === 'dark' ? 'Alternar para modo claro' : 'Alternar para modo escuro'}
      >
        {resolvedTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* Left side - Product experience */}
      <div className="loginScreen-left">
        <div className="loginScreen-glow loginScreen-glow1" />
        <div className="loginScreen-glow loginScreen-glow2" />
        <div className="loginScreen-leftContent">
          <h1 className="loginScreen-title">
            Central inteligente de
            <br />
            atendimento via WhatsApp
          </h1>
          <p className="loginScreen-subtitle">
            Automação com IA, múltiplos atendentes, métricas em tempo real e controle total da operação.
          </p>

          {/* Product mock */}
          <div className="loginScreen-mock">
            <div className="loginScreen-mockMetrics">
              {[
                { Icon: Users, label: 'Leads', value: '1.284' },
                { Icon: BarChart3, label: 'Conversão', value: '31%' },
                { Icon: Bot, label: 'IA ativa', value: '99%' },
                { Icon: CheckCircle, label: 'Disponibilidade', value: '99.9%' },
              ].map((item, i) => {
                const Icon = item.Icon;
                return (
                  <div key={i} className="loginScreen-metric">
                    <div className="loginScreen-metricHeader">
                      <Icon size={16} className="loginScreen-metricIcon" />
                      <span className="loginScreen-metricLabel">{item.label}</span>
                    </div>
                    <span className="loginScreen-metricValue">{item.value}</span>
                  </div>
                );
              })}
            </div>
            <div className="loginScreen-chatMock">
              <div className="loginScreen-chatHeader">
                <span className="loginScreen-chatTitle">Ligchat • Comercial</span>
                <span className="loginScreen-chatSubtitle">Conversa com Lead</span>
              </div>
              <div className="loginScreen-chatBody">
                <div className="loginScreen-bubble loginScreen-bubbleBot">
                  <span className="loginScreen-bubbleTime">09:01</span>
                  Olá Ricardo! 👋 Vi que você quer estruturar seu time comercial no WhatsApp. Quantos atendentes você possui hoje?
                </div>
                <div className="loginScreen-bubble loginScreen-bubbleUser">
                  <span className="loginScreen-bubbleTime">09:02</span>
                  Tenho 5 vendedores e 1 gerente.
                </div>
                <div className="loginScreen-bubble loginScreen-bubbleBot">
                  <span className="loginScreen-bubbleTime">09:03</span>
                  Perfeito. Nosso plano Business com 2 números oficiais e CRM dividido por etapas é o ideal para você.
                </div>
                <div className="loginScreen-bubble loginScreen-bubbleUser">
                  <span className="loginScreen-bubbleTime">09:05</span>
                  Isso já inclui automação de follow-up?
                </div>
                <div className="loginScreen-bubble loginScreen-bubbleBot">
                  <span className="loginScreen-bubbleTime">09:06</span>
                  Sim! A IA envia mensagens automáticas conforme o comportamento do lead.
                </div>
                <div className="loginScreen-bubble loginScreen-bubbleUser">
                  <span className="loginScreen-bubbleTime">09:10</span>
                  Pode ativar sim 👍
                </div>
              </div>
              <div className="loginScreen-chatInput">
                <span>Digite uma mensagem...</span>
                <div className="loginScreen-chatSend">
                  <Send size={14} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="loginScreen-right">
        {isModalVisible && (
          <div className="loginScreen-modal">
            <div className="loginScreen-modalContent">
              <h2>Sessão Expirada</h2>
              <p>Efetue o login novamente.</p>
              <button className="loginScreen-button loginScreen-primaryButton" onClick={handleCloseModal}>
                Fechar
              </button>
            </div>
          </div>
        )}

        <div className="loginScreen-formContainer">
          <div className="loginScreen-logoContainer">
            <img src={resolvedTheme === 'dark' ? LogoDarkVertical : LogoVertical} alt="LigChat Logo" className="loginScreen-logo" />
          </div>
          <div className="loginScreen-welcomeText">
            <h2>Acesse sua conta</h2>
            <p>Entre com suas credenciais para acessar o sistema</p>
          </div>

          <div className="loginScreen-formWrapper">
            <div className="loginScreen-tabs">
              <button
                className={`loginScreen-tab ${activeTab === 'email' ? 'loginScreen-activeTab' : ''}`}
                onClick={() => activeTab === 'email' && setActiveTab('email')}
              >
                Email
              </button>
              <button
                className={`loginScreen-tab ${activeTab === 'code' ? 'loginScreen-activeTab' : ''}`}
                disabled={activeTab === 'email'}
              >
                Código de acesso
              </button>
            </div>

            {activeTab === 'email' ? (
              <form className="loginScreen-form" onSubmit={handleSendEmail}>
                <div className="loginScreen-formItem">
                  <label className="loginScreen-label">Email</label>
                  <input
                    className={`loginScreen-input ${!isEmailValid ? 'loginScreen-inputError' : ''}`}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      validateEmail(e.target.value);
                      setIsEmailValid(true);
                    }}
                    placeholder="Seu email corporativo"
                    type="email"
                  />
                  {!isEmailValid && <p className="loginScreen-errorText">{errorMessage}</p>}
                </div>
                <div className="loginScreen-formItem">
                  <button
                    className="loginScreen-button loginScreen-primaryButton"
                    type="submit"
                    disabled={!isFormValid || loadingEmail}
                  >
                    {loadingEmail ? <span className="loginScreen-loadingSpinner" /> : 'Continuar'}
                  </button>
                </div>
              </form>
            ) : (
              <form className="loginScreen-form" onSubmit={handleSendCode}>
                <div className="loginScreen-formItem">
                  <label className="loginScreen-label">Código de verificação</label>
                  <div className="loginScreen-codeInfo">
                    <p>Enviamos um código de 6 dígitos para <strong>{email}</strong></p>
                  </div>
                  <input
                    className={`loginScreen-input ${!isCodeValid ? 'loginScreen-inputError' : ''}`}
                    value={code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setCode(value);
                      validateCode(value);
                    }}
                    placeholder="Digite o código de 6 dígitos"
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                  />
                  {!isCodeValid && <p className="loginScreen-errorText">{errorMessage}</p>}
                </div>
                <div className="loginScreen-formItem loginScreen-rememberMe">
                  <label className="loginScreen-checkboxLabel">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="loginScreen-checkbox"
                    />
                    <span>Lembrar de mim (token válido por 30 dias)</span>
                  </label>
                </div>
                <div className="loginScreen-formItem">
                  <button
                    className="loginScreen-button loginScreen-primaryButton"
                    type="submit"
                    disabled={!isCodeValid || loadingCode}
                  >
                    {loadingCode ? <span className="loginScreen-loadingSpinner" /> : 'Acessar conta'}
                  </button>
                </div>
                <div className="loginScreen-formItem loginScreen-resendContainer">
                  {timer > 0 ? (
                    <p className="loginScreen-timerText">Aguarde {timer} segundos para reenviar o código</p>
                  ) : (
                    <button type="button" className="loginScreen-linkButton" onClick={handleResendCode}>
                      Reenviar código
                    </button>
                  )}
                </div>
              </form>
            )}

            <div className="loginScreen-links">
              <Link to="/privacy-policy" className="loginScreen-link">Política de Privacidade</Link> |{' '}
              <Link to="/terms-of-use" className="loginScreen-link">Termos de Uso</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
